import React from 'react';
import { useQuery, useMutation } from 'react-query';
import { withStyles } from '@material-ui/core/styles';
import numeral from 'numeral';

import MuiTypography from '@material-ui/core/Typography';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import { Button, Box } from '@material-ui/core';

import MuiGrid from '@material-ui/core/Grid';

import ViewWrapper from 'views/ViewWrapper';
import TableAffiliatePayments from 'components/TableAffiliatePayments';

import * as API from 'api';
import ButtonWithConfirmation from 'components/ButtonWithConfirmation';
const { getTotalPayable, queueCloseAllPayableReferrals } = API.AffiliatePayments();

const { getCompanyStripeBalanceUSD } = API.RoyaltyTransfers();

const styles = theme => ({
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  balance: {
    lineHeight: '1.5rem',
  },
  balanceValue: {
    display: 'inline-flex',
    marginLeft: theme.spacing(0.5),
  },
  startLabel: {
    marginRight: theme.spacing(1),
  },
});

const STRIPE_BALANCE_ERROR_MSG = 'Something went wrong while retrieving the balance';
const Loading = <MuiCircularProgress size={'1rem'} />;
const StripeBalance = ({ dataCompanyStripeBalanceUSD, isLoading, isSuccess }) => {
  if (isLoading) {
    return Loading;
  }

  if (isSuccess) {
    return numeral(dataCompanyStripeBalanceUSD).format('0,0.00');
  }

  return STRIPE_BALANCE_ERROR_MSG;
};

const findStripeTotalPayable = dataTotalPayable => {
  const stripePayableRow = dataTotalPayable.find(row => row.is_payable && row.external_transfer_method === 'Stripe');
  return numeral(stripePayableRow?.sum_post_recoup_usd || 0);
};

const StripeTotalPayable = ({ dataTotalPayable, isLoading, isSuccess }) => {
  if (isLoading) {
    return Loading;
  }

  if (isSuccess) {
    const totalPayable = findStripeTotalPayable(dataTotalPayable);
    return totalPayable.format('0,0.00');
  }

  return STRIPE_BALANCE_ERROR_MSG;
};

const AffiliatePayments = ({ ...props }) => {
  const { classes } = props;

  const {
    data: dataCompanyStripeBalanceUSD,
    refetch: refetchCompanyStripeBalanceUSD,
    isLoading: CompanyStripeBalanceUSDIsLoading,
    isSuccess: CompanyStripeBalanceUSDIsSuccess,
  } = useQuery('CompanyStripeBalanceUSD', getCompanyStripeBalanceUSD, {
    refetchOnWindowFocus: false,
  });

  const {
    data: dataTotalPayable,
    refetch: refetchTotalPayable,
    isLoading: TotalPayableIsLoading,
    isSuccess: TotalPayableIsSuccess,
  } = useQuery('AffiliateTotalPayable', getTotalPayable, {
    refetchOnWindowFocus: false,
  });

  const { mutate: queueCloseAllReferralsMutation, isLoading: isCloseAllReferralsLoading } = useMutation(
    () => queueCloseAllPayableReferrals(),
    {
      onSuccess: data => alert(`${data.job.name} job queued.`),
    }
  );

  function checkIfIsDisabled() {
    return (
      isCloseAllReferralsLoading ||
      !dataCompanyStripeBalanceUSD ||
      !dataTotalPayable ||
      findStripeTotalPayable(dataTotalPayable).value() > Number(dataCompanyStripeBalanceUSD)
    );
  }

  return (
    <ViewWrapper>
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          <MuiTypography className={classes.title} variant="h5">
            Affiliate Payments
          </MuiTypography>
          <div className={classes.actionRow}>
            <div className={classes.balance}>
              <div>
                Venice Stripe Balance:{' '}
                <strong className={classes.balanceValue}>
                  $&nbsp;
                  <StripeBalance
                    dataCompanyStripeBalanceUSD={dataCompanyStripeBalanceUSD}
                    isLoading={CompanyStripeBalanceUSDIsLoading}
                    isSuccess={CompanyStripeBalanceUSDIsSuccess}
                  />
                </strong>
              </div>
              <div>
                Total Payable Via Stripe:{' '}
                <strong className={classes.balanceValue}>
                  $&nbsp;
                  <StripeTotalPayable
                    dataTotalPayable={dataTotalPayable}
                    isLoading={TotalPayableIsLoading}
                    isSuccess={TotalPayableIsSuccess}
                  />
                </strong>
              </div>
            </div>
          </div>
          <Box display="flex" justifyContent="right">
            <Button
              variant="contained"
              size="small"
              className={classes.actionButton}
              onClick={() => window.open('https://affiliates.venicemusic.co/a/payments/', '_blank')}>
              Review Payments
            </Button>
            <ButtonWithConfirmation
              disabled={checkIfIsDisabled()}
              variant="contained"
              size="small"
              className={classes.actionButton}
              buttonText="Execute All Stripe Transfers"
              dialogTitle="Execute All Stripe Transfers"
              dialogContent="Are you sure you want to execute all stripe transfers?"
              onSubmit={queueCloseAllReferralsMutation}
            />
          </Box>
          <TableAffiliatePayments
            refetchCompanyStripeBalanceUSD={refetchCompanyStripeBalanceUSD}
            refetchTotalPayable={refetchTotalPayable}
            showIssues={true}
          />
        </MuiPaper>
      </MuiGrid>
    </ViewWrapper>
  );
};

export default withStyles(styles)(AffiliatePayments);
